import React, { useState, useMemo, useRef, useEffect } from "react";
import API_URL from "../config";
const API_BASE = `${API_URL}/api`;

export default function AddExpense({ entrepriseId}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const [queryNom, setQueryNom] = useState("");
  const [selectedNom, setSelectedNom] = useState("");
  const [openNomList, setOpenNomList] = useState(false);
  const [highlightNomIndex, setHighlightNomIndex] = useState(-1);

  const [prixUnitaire, setPrixUnitaire] = useState("");
  const [quantite, setQuantite] = useState("");
  const [date, setDate] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);

  const nomInputRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Fetch suggestions
  useEffect(() => {
    if (!queryNom.trim()) return setSuggestions([]);
    const fetchSuggestions = async () => {
      try {
        setLoadingSuggestions(true);
        const res = await fetch(
          `${API_BASE}/depense-noms?q=${encodeURIComponent(queryNom)}&entrepriseId=${entrepriseId}`
        );
        if (!res.ok) throw new Error("Erreur chargement suggestions");
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erreur fetchSuggestions:", err);
      } finally {
        setLoadingSuggestions(false);
      }
    };
    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [queryNom, entrepriseId]);

  // Filter suggestions
  const filteredNoms = useMemo(() => {
    const q = queryNom.trim().toLowerCase();
    if (!q) return suggestions;
    const contains = suggestions.filter((o) => o.toLowerCase().includes(q));
    const starts = contains.filter((o) => o.toLowerCase().startsWith(q));
    const others = contains.filter((o) => !o.toLowerCase().startsWith(q));
    return [...starts, ...others];
  }, [queryNom, suggestions]);

  const selectNom = (nom) => {
    setSelectedNom(nom);
    setQueryNom(nom);
    setOpenNomList(false);
    setHighlightNomIndex(-1);
    setErrorMsg("");
    setTimeout(() => {
      document.getElementById("prixUnitaireInput")?.focus();
    }, 0);
  };

  const onKeyDownNom = (e) => {
    if (!openNomList && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpenNomList(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightNomIndex((i) => Math.min(i + 1, filteredNoms.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightNomIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (openNomList) {
        e.preventDefault();
        if (highlightNomIndex >= 0) selectNom(filteredNoms[highlightNomIndex]);
        else if (filteredNoms.length === 1) selectNom(filteredNoms[0]);
      }
    } else if (e.key === "Escape") {
      setOpenNomList(false);
    }
  };

  // Dropdown fixed position
  useEffect(() => {
    if (!openNomList || !nomInputRef.current) return;
    const rect = nomInputRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
  }, [openNomList, queryNom]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (nomInputRef.current && !nomInputRef.current.contains(e.target)) {
        setOpenNomList(false);
        setHighlightNomIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isNomValid = queryNom.trim().length > 0;
  const canSubmit =
    isNomValid &&
    prixUnitaire !== "" &&
    Number(prixUnitaire) > 0 &&
    quantite !== "" &&
    Number(quantite) > 0 &&
    !!date;

  const addExpense = async (e) => {
    e.preventDefault();
    if (!isNomValid) {
      setErrorMsg("Veuillez saisir le nom de la dépense.");
      nomInputRef.current?.focus();
      return;
    }
    if (!canSubmit) {
      setErrorMsg("Veuillez remplir correctement tous les champs.");
      return;
    }
    try {
      setLoadingAction(true);
      const payload = {
        entrepriseId,
        nom: queryNom,
        prix_unitaire: parseFloat(prixUnitaire),
        quantite: parseInt(quantite, 10),
        date,
      };
      const res = await fetch(`${API_BASE}/depenses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowSuccess(true);
        setSelectedNom("");
        setQueryNom("");
        setPrixUnitaire("");
        setQuantite("");
        setDate("");
        setErrorMsg("");
      } else {
        const { message } = await res.json().catch(() => ({ message: "" }));
        setErrorMsg(message || "Erreur lors de l'ajout de la dépense.");
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Impossible de se connecter au serveur.");
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h1 style={styles.title}>Ajouter une nouvelle dépense</h1>
          <p style={styles.subtitle}>
            Tapez le <b>nom de la dépense</b>. Sélectionnez une suggestion ou saisissez un nouveau nom.
          </p>
        </div>
        <form onSubmit={addExpense} style={styles.form}>
          <label style={styles.label}>
            Nom de la dépense <span style={styles.required}>*</span>
          </label>
          <input
            ref={nomInputRef}
            type="text"
            placeholder={loadingSuggestions ? "Chargement des suggestions…" : "Ex: Loyer, Achat fournitures…"}
            value={queryNom}
            onChange={(e) => { setQueryNom(e.target.value); setOpenNomList(true); setHighlightNomIndex(-1); setSelectedNom(""); setErrorMsg(""); }}
            onKeyDown={onKeyDownNom}
            style={{ ...styles.input, ...(queryNom && !isNomValid ? styles.inputWarn : {}) }}
          />
          {/* Dropdown en position fixed */}
          {openNomList && filteredNoms.length > 0 && (
            <ul
              style={{
                ...styles.comboList,
                position: "fixed",
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
                zIndex: 9999,
              }}
            >
              {filteredNoms.map((opt, idx) => (
                <li
                  key={`${opt}-${idx}`}
                  style={{ ...styles.comboItem, ...(idx === highlightNomIndex ? styles.comboItemActive : {}) }}
                  onMouseDown={(e) => { e.preventDefault(); selectNom(opt); }}
                  onMouseEnter={() => setHighlightNomIndex(idx)}
                >
                  {opt}
                </li>
              ))}
            </ul>
          )}

          <label style={styles.label} htmlFor="prixUnitaireInput">Prix unitaire (FCFA) <span style={styles.required}>*</span></label>
          <input
            id="prixUnitaireInput"
            type="number"
            min="0"
            step="any"
            placeholder="Ex: 1500"
            value={prixUnitaire}
            onChange={(e) => setPrixUnitaire(e.target.value)}
            style={styles.input}
          />

          <label style={styles.label}>Quantité <span style={styles.required}>*</span></label>
          <input
            type="number"
            min="1"
            step="1"
            placeholder="Ex: 3"
            value={quantite}
            onChange={(e) => setQuantite(e.target.value)}
            style={styles.input}
          />

          <label style={styles.label}>Date <span style={styles.required}>*</span></label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.input}
          />

          {errorMsg && <div style={styles.errorBox}>{errorMsg}</div>}

          <button type="submit" style={{ ...styles.button, ...(canSubmit ? {} : styles.buttonDisabled) }} disabled={!canSubmit || loadingAction}>
            {loadingAction ? "Ajout en cours..." : "+ Ajouter la dépense"}
          </button>
        </form>
      </div>

      {showSuccess && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.successIcon}>✅</div>
            <h2>Dépense ajoutée !</h2>
            <p>Votre dépense a été enregistrée avec succès.</p>
            <button style={styles.closeButton} onClick={() => setShowSuccess(false)}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}

// === Styles ===
const styles = {
  page: { minHeight: "100vh", background: "linear-gradient(135deg, rgba(76,175,239,0.12) 0%, rgba(99,102,241,0.12) 100%)", padding: "48px 16px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Inter,Helvetica,Arial,sans-serif' },
  card: { width: "100%", maxWidth: 560, borderRadius: 16, background: "#fff", boxShadow: "0 20px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  cardHeader: { padding: "20px 24px", background: "linear-gradient(135deg, rgba(76,175,239,0.15) 0%, rgba(99,102,241,0.15) 100%)", borderBottom: "1px solid rgba(0,0,0,0.06)" },
  title: { margin: 0, fontSize: 22, color: "#1f2937", fontWeight: 700 },
  subtitle: { margin: "6px 0 0", fontSize: 14, color: "#4b5563" },
  form: { display: "grid", gridTemplateColumns: "1fr", gap: 14, padding: 24 },
  label: { fontSize: 13, color: "#374151", fontWeight: 600 },
  required: { color: "#ef4444", marginLeft: 4 },
  input: { padding: "12px 14px", fontSize: 15, borderRadius: 10, border: "1px solid #e5e7eb", outline: "none", transition: "box-shadow .15s, border-color .15s", background: "#fff", boxShadow: "0 1px 0 rgba(0,0,0,0.02)" },
  inputWarn: { borderColor: "#f59e0b", boxShadow: "0 0 0 3px rgba(245,158,11,0.15)" },
  comboList: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, maxHeight: 240, overflowY: "auto", boxShadow: "0 12px 24px rgba(0,0,0,0.08)" },
  comboItem: { padding: "10px 12px", cursor: "pointer", fontSize: 14, color: "#111827" },
  comboItemActive: { background: "#f3f4f6" },
  errorBox: { background: "rgba(239, 68, 68, 0.08)", color: "#b91c1c", border: "1px solid rgba(239, 68, 68, 0.25)", padding: "10px 12px", borderRadius: 10, fontSize: 14 },
  button: { marginTop: 6, padding: "12px 16px", background: "linear-gradient(135deg, #4cafef 0%, #6366f1 100%)", color: "white", border: "none", borderRadius: 12, cursor: "pointer", fontSize: 16, fontWeight: 700, boxShadow: "0 10px 20px rgba(99,102,241,0.25)", transition: "transform .06s ease-in-out, box-shadow .15s" },
  buttonDisabled: { opacity: 0.6, cursor: "not-allowed", boxShadow: "none" },
  modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: 16 },
  modal: { width: "100%", maxWidth: 420, background: "#fff", padding: 24, borderRadius: 16, boxShadow: "0 20px 40px rgba(0,0,0,0.15)", textAlign: "center" },
  successIcon: { fontSize: 36, marginBottom: 8 },
  closeButton: { marginTop: 18, padding: "10px 16px", background: "#111827", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 14 },
};
